import React, { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import './App.css';
import { AppBar, Box, Button, Stack, Tab, Tabs, Typography } from '@mui/material';
import Config from '../config/config';
import Footer from './Footer';
import OverviewTab from './tabs/OverviewTab';
import CertificatesTab from './tabs/CertificatesTab';
import RolesAnywhereTab from './tabs/RolesAnywhereTab';
import AssistantPane from './AssistantPane';
import logo from '../assets/nakomis-logo.png';

const App: React.FC = () => {
    const auth = useAuth();
    const [tab, setTab] = useState(0);

    const signOutRedirect = () => {
        const logoutUrl = `https://${Config.cognito.cognitoDomain}/logout`
            + `?client_id=${Config.cognito.userPoolClientId}`
            + `&logout_uri=${encodeURIComponent(Config.cognito.logoutUri)}`;
        auth.removeUser();
        window.location.href = logoutUrl;
    };

    if (auth.isLoading) {
        return (
            <Box className='App' sx={{ display: 'flex', minHeight: '60vh', alignItems: 'center', justifyContent: 'center' }}>
                Loading...
            </Box>
        );
    }

    if (auth.error) {
        return (
            <Box className='App' sx={{ display: 'flex', minHeight: '60vh', alignItems: 'center', justifyContent: 'center' }}>
                Error: {auth.error.message}
            </Box>
        );
    }

    if (auth.isAuthenticated) {
        return (
            <div className='App'>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', minHeight: '100vh' }}>
                    {/* Main column: app bar, tabs and the active tab's content. */}
                    <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                        <AppBar position='static'>
                            <Stack direction='row' alignItems='center' spacing={1.5} sx={{ px: 2, py: 1.5 }}>
                                <Box component='img' src={logo} alt='' sx={{ height: 32, width: 32 }} />
                                <Typography variant='h6' sx={{ flexGrow: 1, fontWeight: 600 }}>
                                    Nakomis Home
                                </Typography>
                                <Button variant='contained' size='small' onClick={signOutRedirect}>
                                    Sign out
                                </Button>
                            </Stack>
                            <Tabs
                                value={tab}
                                onChange={(_e, v) => setTab(v)}
                                textColor='inherit'
                                indicatorColor='primary'
                                sx={{ minHeight: 40 }}
                            >
                                <Tab label='Overview' />
                                <Tab label='Certificates' />
                                <Tab label='Roles Anywhere' />
                            </Tabs>
                        </AppBar>
                        <Box sx={{ p: 3, pb: 6, flexGrow: 1 }}>
                            {tab === 0 && <OverviewTab />}
                            {tab === 1 && <CertificatesTab />}
                            {tab === 2 && <RolesAnywhereTab />}
                        </Box>
                    </Box>
                    {/* Persistent assistant, outside the tab group so it stays put
                        while you switch tabs. Collapsible to a slim rail. */}
                    <AssistantPane />
                </Box>
                <Footer />
            </div>
        );
    }

    return (
        <div className='App'>
            <Stack
                className='App-header'
                alignItems='center'
                justifyContent='center'
                spacing={2}
                sx={{ minHeight: '60vh', p: 4, textAlign: 'center' }}
            >
                <Box component='img' src={logo} alt='' sx={{ height: 56, width: 56 }} />
                <Typography variant='h4' sx={{ fontWeight: 600 }}>Nakomis Home</Typography>
                <Typography variant='body2' color='text.secondary'>Admin portal</Typography>
                <Button variant='contained' onClick={() => auth.signinRedirect()}>
                    Sign in
                </Button>
            </Stack>
            <Footer />
        </div>
    );
};

export default App;
